import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { QueueItem, Subject, TodayResponse, User } from '../api/types'
import { GoalsCard } from '../components/GoalsCard'
import { ScheduleCard } from '../components/ScheduleCard'
import { Icon } from '../components/Icon'
import { NewDealModal } from '../components/NewDealModal'
import { PlaybookModal } from '../components/Playbooks'
import { ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { initials, relativeDays, shortDate, usd, usdShort } from '../lib/format'
import { subjectOfLoan } from '../lib/subject'
import { TONES, tint } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

/** True once an estimated close date is before today (not merely earlier today). */
function isPastDue(iso: string | null): boolean {
  if (!iso) return false
  const d = new Date(iso)
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  return !Number.isNaN(d.getTime()) && d < startOfToday
}

function greeting() {
  const h = new Date().getHours()
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'
}

function subjectOf(q: QueueItem): Subject | null {
  if (q.kind === 'loan') return subjectOfLoan(q.id)
  if (q.kind === 'person') return { person_key: q.id }
  return null
}

/** The home screen is a ranked work queue, not a chart dashboard: what will cost
 *  money if it is ignored today. The order comes from the server — the client
 *  renders it verbatim and never re-sorts. */
export function Today({ user }: { user: User }) {
  const [limit, setLimit] = useState(5)
  const { data, error, loading, reload } = useAsync<TodayResponse>(() => api.today(limit), [limit])
  const [why, setWhy] = useState(false)
  const [playbookFor, setPlaybookFor] = useState<Subject | null>(null)
  const [newDeal, setNewDeal] = useState(false)
  const nav = useNavigate()
  const toast = useToast()

  if (loading && !data) return <div className="page"><Loading label="Working out what needs you…" /></div>
  if (error || !data) return <div className="page"><ErrorState error={error} onRetry={reload} /></div>

  const first = (user.name ?? '').split(/\s+/)[0]
  const s = data.summary
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })

  const kpis = [
    { icon: 'account_balance', label: 'Active pipeline', value: usdShort(s.loans.active_volume), delta: `${s.loans.active} loans`, tone: 'var(--ink-2)', sub: 'mirrored from LendingPad' },
    { icon: 'verified', label: 'Clear to close', value: String(s.loans.ctc), delta: s.loans.ctc ? 'ready' : '—', tone: s.loans.ctc ? 'var(--positive)' : 'var(--ink-3)', sub: 'awaiting signing' },
    { icon: 'task_alt', label: 'Open tasks', value: String(s.open_tasks), delta: s.recent_replies ? `${s.recent_replies} new replies` : '', tone: 'var(--accent)', sub: 'assigned to you', href: '/tasks' },
    { icon: 'bolt', label: 'Needs you', value: String(data.queue_total), delta: data.queue_total ? 'today' : 'clear', tone: data.queue_total ? 'var(--danger)' : 'var(--positive)', sub: `locks ≤${data.queue_rules.lock_days}d · stalled ≥${data.queue_rules.stall_days}d` },
  ]

  async function completeTask(q: QueueItem) {
    try {
      await api.updateTask(Number(q.id), { done: true })
      toast('Task done', { tone: 'positive', action: { label: 'Undo', onClick: () => api.updateTask(Number(q.id), { done: false }).then(reload) } })
      reload()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  async function snooze(q: QueueItem) {
    // A follow-up task tomorrow is the honest version of "remind me later": it lands
    // on the record, and the officer's task list, rather than a hidden flag.
    const subject = subjectOf(q)
    try {
      const d = new Date()
      d.setDate(d.getDate() + 1)
      await api.createTask({ title: `Follow up: ${q.trigger_label.toLowerCase()} — ${q.name}`, due: d.toISOString().slice(0, 10), subject: subject ?? undefined })
      toast('Follow-up task added for tomorrow', { tone: 'positive' })
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    }
  }

  const disabledRules = new Set(data.queue_disabled)
  const focus = data.queue[0]

  return (
    <div className="page today-page">
      <header className="dashboard-heading"><div><h1>{greeting()}, {first}.</h1><p>{today}</p></div><button className="btn primary" onClick={() => setNewDeal(true)}><Icon name="add" size={17} /> Create opportunity</button></header>
      <GoalsCard />

      {data.messaging.mode === 'simulated' ? (
        <div className="notice warning" style={{ marginBottom: 14 }}>
          <Icon name="science" />
          <span>Messaging is in <strong>simulated mode</strong>: texts and emails are checked and logged but not delivered.</span>
        </div>
      ) : null}

      <div className="brief-lead-grid">
        <section className="pipeline-story" aria-label="Active pipeline">
          <div className="story-meta"><span>ACTIVE PIPELINE</span><Icon name="north_east" size={20} /></div>
          <div className="pipeline-story-value">{usdShort(s.loans.active_volume)}</div>
          <div className="pipeline-story-foot"><span>{s.loans.active} active loans<br /><strong>{s.loans.ctc} ready to close</strong></span><Link to={user.is_admin ? '/pipeline' : '/leads'} className="round-link" aria-label="Explore your pipeline"><Icon name="arrow_forward" size={24} /></Link></div>
        </section>
        <section className="focus-story">
          <div className="story-meta"><span>NEXT PRIORITY</span><span className="focus-counter">{data.queue_total} to focus on</span></div>
          {focus ? <>
            <span className="focus-trigger" style={TONES[focus.tone]}><span />{focus.trigger_label}</span>
            <h2>{focus.name}</h2><p>{focus.detail}</p>
            <div className="focus-story-foot"><button className="btn focus-open" onClick={() => nav(focus.href)}>Open {focus.kind === 'loan' ? 'file' : focus.kind === 'task' ? 'task' : 'contact'}<Icon name="arrow_forward" size={18} /></button><button className="focus-snooze" onClick={() => focus.kind === 'task' ? completeTask(focus) : snooze(focus)}><Icon name={focus.kind === 'task' ? 'check' : 'schedule'} size={16} />{focus.kind === 'task' ? 'Mark done' : 'Tomorrow'}</button></div>
          </> : <><span className="focus-trigger">ALL CAUGHT UP</span><h2>Room to<br /><em>build something.</em></h2><p>Your queue is clear. Make your next connection.</p><Link className="btn focus-open" to="/contacts">Explore your people <Icon name="arrow_forward" size={18} /></Link></>}
        </section>
      </div>
      <div className="brief-metrics">
        {kpis.slice(1).map(k => <Link to={k.label === 'Needs you' ? '/#action-list' : k.href ?? (user.is_admin ? '/pipeline' : '/leads')} onClick={k.label === 'Needs you' ? () => document.getElementById('action-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) : undefined} className="brief-metric" key={k.label}><span className="brief-metric-icon"><Icon name={k.icon} size={20} /></span><span className="brief-metric-value">{k.value}</span><span className="brief-metric-copy">{k.label}<small>{k.sub}</small></span><Icon name="north_east" size={15} /></Link>)}
      </div>
      <div className="overview-section"><h2>Priority work</h2><button className="text-action" onClick={() => nav('/customize')}><Icon name="tune" size={15} /> Make it yours</button></div>
      <div className="body-2col">
        <div className="card priority-card" id="action-list">
          <div className="card-head" style={{ position: 'relative' }}>
            <div><span className="panel-overline">YOUR ACTION LIST</span><h2 className="h2">A few things worth your attention</h2></div>
            <span className="spacer" />
            <button className="btn sm" onClick={() => setWhy((v) => !v)} aria-expanded={why}>
              <Icon name="help" size={15} /> Why these?
            </button>
            {why ? (
              <div className="menu" style={{ top: '100%', right: 12, width: 340, padding: 12 }} onMouseLeave={() => setWhy(false)}>
                <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--ink-2)' }}>
                  Each item matched one of these rules. Higher-priority rules come first; ties go to the bigger loan.
                </p>
                {([
                  ['Lock expiring', `rate lock ends within ${data.queue_rules.lock_days} days`, 'lock_expiring'],
                  ['Task overdue', 'a task of yours is past due', 'task_overdue'],
                  ['Blocking condition', 'a requested condition is not received', 'blocking_condition'],
                  ['Clear to close', 'ready to schedule signing', 'clear_to_close'],
                  ['Task due today', 'a task of yours is due today', 'task_due'],
                  ['Stalled', `no stage change in ${data.queue_rules.stall_days} days`, 'stalled'],
                  ['Partner quiet', `no loan from a partner in ${data.queue_rules.quiet_days} days`, 'partner_quiet'],
                ] as [string, string, string][]).map(([label, rule, key]) => (
                  <div key={key} style={{ display: 'flex', gap: 8, fontSize: 12.5, padding: '4px 0', opacity: disabledRules.has(key) ? 0.45 : 1 }}>
                    <strong style={{ width: 118, flex: 'none' }}>{label}</strong>
                    <span style={{ color: 'var(--ink-2)' }}>{disabledRules.has(key) ? 'turned off' : rule}</span>
                  </div>
                ))}
                <Link to="/customize?tab=today" style={{ fontSize: 12.5, display: 'inline-block', marginTop: 8 }}>Change what shows up here →</Link>
              </div>
            ) : null}
          </div>

          {data.queue.length === 0 ? (
            <div className="state clear">
              <Icon name="check_circle" size={30} />
              <h3 style={{ marginTop: 10 }}>Nothing needs you today</h3>
              <p>No locks expiring, no blocking conditions, no stalled files, no overdue tasks.</p>
            </div>
          ) : (
            data.queue.map((q) => {
              const tone = TONES[q.tone]
              const subject = subjectOf(q)
              return (
                <div className="qrow" key={`${q.kind}-${q.id}`} role="link" tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => nav(q.href)}
                  onKeyDown={(e) => { if (e.key === 'Enter') nav(q.href) }}>
                  <span className="qav" style={tint(q.name)}>{initials(q.name)}</span>
                  <span className="qmid">
                    <span className="qname-row">
                      <span className="qname">{q.name}</span>
                      <span className="chip-micro" style={tone}>{q.trigger_label}</span>
                    </span>
                    <span className="qdetail">{q.detail}</span>
                  </span>
                  <span className="qval">
                    <span className="qval-n">{q.value !== null ? usd(q.value) : ''}</span>
                    <span className="qval-sub" style={{ display: 'block' }}>{q.value_sub}</span>
                  </span>
                  <span className="qacts" onClick={(e) => e.stopPropagation()}>
                    {q.kind === 'task' ? (
                      <button className="iconbtn" title="Mark done" onClick={() => completeTask(q)}>
                        <Icon name="check" size={18} />
                      </button>
                    ) : (
                      <button className="iconbtn" title="Remind me tomorrow (adds a task)" onClick={() => snooze(q)}>
                        <Icon name="snooze" size={18} />
                      </button>
                    )}
                    {subject ? (
                      <button className="iconbtn" title="Start a workflow" onClick={() => setPlaybookFor(subject)}>
                        <Icon name="bolt" size={18} />
                      </button>
                    ) : null}
                    <button className="iconbtn" title="Open" onClick={() => nav(q.href)}>
                      <Icon name="arrow_forward" size={18} />
                    </button>
                  </span>
                </div>
              )
            })
          )}

          {data.queue_total > data.queue.length ? (
            <button className="card-foot" style={{ width: '100%', border: 0, borderTop: '1px solid var(--line-soft)', background: 'none', cursor: 'pointer', textAlign: 'left' }}
              onClick={() => setLimit(50)}>
              {loading ? 'Loading…' : `Show ${Math.min(45, data.queue_total - data.queue.length)} more`}
            </button>
          ) : limit > 5 && data.queue.length > 5 ? (
            <button className="card-foot" style={{ width: '100%', border: 0, borderTop: '1px solid var(--line-soft)', background: 'none', cursor: 'pointer', textAlign: 'left' }}
              onClick={() => setLimit(5)}>
              Show fewer
            </button>
          ) : null}
        </div>

        <div className="stack">
          <ScheduleCard />


          <div className="card">
            <div className="card-head">
              <h2 className="h2" style={{ fontSize: 14.5 }}>Closing soon</h2>
              <span className="spacer" />
              <span className="mono" style={{ fontSize: 13, fontWeight: 500 }}>
                {usdShort(data.closing.reduce((a, c) => a + (c.loan_amount ?? 0), 0))}
              </span>
            </div>
            {data.closing.length ? (
              data.closing.map((c) => {
                const open = Object.values(c.processing ?? {}).filter((p) => p && !p.completed).length
                const pct = open === 0 ? 92 : open === 1 ? 74 : 55
                const tone = open === 0 ? 'var(--positive)' : open === 1 ? 'var(--accent)' : 'var(--warning)'
                const late = isPastDue(c.closing)
                return (
                  <Link to={`/loans/${encodeURIComponent(c.loan_id)}`} className="listrow" key={c.loan_id} style={{ display: 'block', color: 'inherit' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{c.borrower}</span>
                      {/* A past date on an unfunded loan is a missed close, not an upcoming one. */}
                      <span className="mono" style={{ fontSize: 12, color: late ? 'var(--danger)' : undefined }}>{shortDate(c.closing)}</span>
                    </div>
                    <div className="bar-track" style={{ margin: '7px 0 5px' }}>
                      <div className="bar-fill" style={{ width: `${pct}%`, background: tone }} />
                    </div>
                    <div style={{ fontSize: 11.5, color: late ? 'var(--danger)' : 'var(--ink-3)' }}>
                      {late
                        ? `Est. close passed · not funded${open ? ` · ${open} open` : ''}`
                        : open === 0 ? 'All conditions cleared' : `${open} condition${open === 1 ? '' : 's'} outstanding`}
                    </div>
                  </Link>
                )
              })
            ) : (
              <div className="card-body quiet">No estimated closings in the next three weeks.</div>
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <Icon name="handshake" size={18} style={{ color: 'var(--accent)' }} />
              <h2 className="h2" style={{ fontSize: 14.5 }}>Top referral partners</h2>
              <span className="spacer" />
              <Link to="/partners" className="quiet" style={{ fontSize: 11.5 }}>All partners →</Link>
            </div>
            {data.partners.length ? (
              data.partners.map((p) => {
                const max = Math.max(...data.partners.map((x) => x.volume || 1))
                return (
                  <Link to={`/partners/${encodeURIComponent(p.person_key)}`} className="listrow" key={p.person_key} style={{ color: 'inherit' }}>
                    <span className="circle-av sm" style={tint(p.name)}>{initials(p.name)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, display: 'block' }}>{p.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.company} · {relativeDays(p.last_activity)}</span>
                    </span>
                    <span style={{ width: 56, flex: 'none' }}>
                      <span className="bar-track" style={{ height: 5, display: 'block' }}>
                        <span className="bar-fill" style={{ width: `${Math.round(100 * (p.volume || 0) / max)}%`, background: 'var(--accent)', display: 'block' }} />
                      </span>
                    </span>
                    <span className="mono" style={{ fontSize: 12, flex: 'none' }}>{p.loans}</span>
                  </Link>
                )
              })
            ) : (
              <div className="card-body quiet">No referral partners on file yet.</div>
            )}
          </div>
        </div>
      </div>

      {playbookFor ? (
        <PlaybookModal open subject={playbookFor} onClose={() => setPlaybookFor(null)} onEnrolled={reload} />
      ) : null}
      <NewDealModal open={newDeal} onClose={() => setNewDeal(false)} />
    </div>
  )
}
